import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";

import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { useGetRulesByPlantQuery } from "../../api/services/ruleApi";
import {
  useAddApproverMutation,
  useGetManagersByPlantQuery,
} from "../../api/services/approverApi";
import { useLazyGetAllPlantsDetailsQuery } from "../../api/services/plantApi";
import Swal from "sweetalert2";
import React from "react";
import { LoadingButton } from "@mui/lab";
import {
  ApproverFormSchema,
  type ApproverFormSchemaType,
} from "./approverForm.schema";
import { zodResolver } from "../../utils/zodResolver";

const ApprovelForm: React.FC = () => {
  const navigate = useNavigate();
  const [addApprover, { isLoading }] = useAddApproverMutation();
  const [selectedPlant, setSelectedPlant] = React.useState("");

  // const { data: ruleResponse } = useGetRulesByPlantQuery({ page: 1, limit: 1000 });
  // const rules = ruleResponse?.rules || [];
  const { data: ruleResponse, isLoading: rulesLoading } =
  useGetRulesByPlantQuery(selectedPlant, {
    skip: !selectedPlant, // 🔥 key fix
  });

  const rules = ruleResponse?.rules || [];


  const [getPlants, { data: plantsData }] = useLazyGetAllPlantsDetailsQuery();
  const plants = plantsData?.plants || [];


  const { data: managers, isLoading: managersLoading } =
    useGetManagersByPlantQuery(selectedPlant, { skip: !selectedPlant });

  // Fetch plants
  React.useEffect(() => {
    getPlants({});
  }, []);
  React.useEffect(() => {
    
  },[selectedPlant] );

  // console.log("plants", plants, selectedPlant);
  // console.log("rules", rules);
  // console.log("managers", managers);


  // ⬇ react-hook-form + Zod integrated like reference Plant form
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<ApproverFormSchemaType>({
    resolver: zodResolver(ApproverFormSchema),
    mode: "onBlur",
    defaultValues: {
      plant: "",
      ruleId: "",
      approverName: "",
      approverId: "",
    },
  });

  const onSubmit = async (data: ApproverFormSchemaType) => {
    try {
      const payload: any = {
        plantName: data.plant,
        ruleId: Number(data.ruleId),
        approverName: data.approverName,
        approverId: Number(data.approverId),
      };

      await addApprover(payload).unwrap();

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Approver assigned successfully",
      });

      navigate("/approver");
    } catch (err: any) {
      Swal.fire({
        icon: "error",
        title: "Failed to submit",
        text: err?.data?.message || "Something went wrong.",
      });
    }
  };

  const handleApproverChange = (val: string) => {
    const sel = managers?.find((m: any) => m.user.USERNAME === val);
    setValue("approverId", sel?.user.ID?.toString() || "");
  };

  return (
    <Box height={"100vh"}>
      {/* Top Header */}
      <Stack direction="row" alignItems="center" bgcolor="white" mb={2} px={2}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackOutlinedIcon />
        </IconButton>

        <Box p={2}>
          <Typography variant="h6" fontWeight={600}>
            Add New Approver
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All spare part requests approved by the manager will appear here for
            your review and processing.{" "}
          </Typography>
        </Box>
      </Stack>

      {/* Form Wrapper */}
      <Paper
        sx={{
          p: 3,
          maxWidth: 600,
          backgroundColor: "background.default",
          boxShadow: "none",
          borderRadius: 3,
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          {/*  Plant */}
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 14,
              color: "rgba(109, 110, 112, 1)",
            }}
          >
            Assign Plant
          </Typography>
          <Controller
            name="plant"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth margin="normal" error={!!errors.plant}>
                <Select
                  {...field}
                  value={field.value}
                  displayEmpty
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setSelectedPlant(e.target.value);
                  }}
                  sx={{ bgcolor: "white", color: "rgba(53, 54, 55, 1)" }}
                >
                  <MenuItem value="">Choose Plant</MenuItem>
                  {plants.map((p: any) => (
                    <MenuItem key={p.name} value={p.name}>
                      {p.description}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.plant?.message}</FormHelperText>
              </FormControl>
            )}
          />

          {/*  Rule */}
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 14,
              color: "rgba(109, 110, 112, 1)",
            }}
          >
            Select Rule
          </Typography>
          <Controller
            name="ruleId"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth margin="normal" error={!!errors.ruleId}>
                <Select
                  {...field}
                  value={field.value}
                  displayEmpty
                  disabled={!selectedPlant || rulesLoading}
                  sx={{ bgcolor: "white", color: "rgba(53, 54, 55, 1)" }}
                >
                  <MenuItem value="">
                    {rulesLoading
                      ? "Loading rules..."
                      : selectedPlant
                      ? "Choose Approval Rule"
                      : "Select Plant First"}
                  </MenuItem>

                  {rules.map((r: any) => (
                    <MenuItem key={r.ID} value={String(r.ID)}>
                      {r.WORK_CENTER} - {r.MATERIAL_CATEGORY} - L{r.APPROVAL_LEVEL}
                    </MenuItem>
                  ))}
                </Select>

                <FormHelperText>{errors.ruleId?.message}</FormHelperText>
              </FormControl>
            )}
          />

          {/*  Approver Name */}
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 14,
              color: "rgba(109, 110, 112, 1)",
            }}
          >
            Approver Name
          </Typography>
          <Controller
            name="approverName"
            control={control}
            render={({ field }) => (
              <FormControl
                fullWidth
                margin="normal"
                error={!!errors.approverName}
              >
                <Select
                  {...field}
                  displayEmpty
                  disabled={!selectedPlant || managersLoading}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    handleApproverChange(e.target.value);
                  }}
                  sx={{ bgcolor: "white", color: "rgba(53, 54, 55, 1)" }}
                >
                  <MenuItem value="">
                    {managersLoading
                      ? "Loading..."
                      : selectedPlant
                      ? "Choose Approver"
                      : "Select Plant First"}
                  </MenuItem>

                  {managers?.map((m: any) => (
                    <MenuItem key={m.user.ID} value={m.user.USERNAME}>
                      {m.user.USERNAME}
                    </MenuItem>
                  ))}
                </Select>

                <FormHelperText>{errors.approverName?.message}</FormHelperText>
              </FormControl>
            )}
          />

          {/*  Submit */}
          <Stack direction="row" justifyContent="space-between" mt={4}>
            <Button
              variant="outlined"
              onClick={() => {
                reset();
                setSelectedPlant("");
              }}
              sx={{
                border: "none",
                backgroundColor: "rgba(0, 32, 96, 0.1)",
                color: "rgba(0, 32, 96, 1)  ",
                textTransform: "none",
              }}
            >
              Cancel
            </Button>

            <LoadingButton
              type="submit"
              variant="contained"
              loading={isLoading}
              loadingPosition="center"
              sx={{
                background: "#002060",
                textTransform: "none",
              }}
            >
              Assign Approver
            </LoadingButton>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default ApprovelForm;
